import { useActivateAccount, useGetInvitation } from 'api/auth/hooks';
import { AuthFooter, Form, Wrapper } from 'components';
import FullPageCardContainer from 'components/FullPageCardContainer/FullPageCardContainer';
import { useSearchParams } from 'hooks';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import styled from 'styled-components';
import * as Yup from 'yup';
import { REGISTER } from 'constants/routes';

const BrandInfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 3px solid #be1622;
  background: #f8fafb;
  border-radius: 0 6px 6px 0;
`;

const BrandInfoRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 16px;
  color: #000;
`;

const BrandInfoLabel = styled.span`
  min-width: 56px;
  flex-shrink: 0;
`;

const BrandInfoValue = styled.span`
  font-weight: bold;
  word-break: break-all;
`;

const AccountsSection = styled.div`
  margin-top: 8px;
  position: relative;
`;

const AccountsSectionHeader = styled.p`
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: bold;
  color: #65757d;
  text-transform: uppercase;
`;

const AccountsWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const AccountsScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const AccountCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  width: 230px;
  background: #f8fafb;
  border: 1px solid #ced4da;
  flex-shrink: 0;
  border-radius: 6px;
`;

const AccountAvatar = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  background: #be1622;
  border-radius: 50%;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
`;

const AccountName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1a2b33;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountMeta = styled.span`
  font-size: 12px;
  color: #65757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScrollButtonBase = styled.button`
  position: absolute;
  top: 50%;
  z-index: 3;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const ScrollButtonLeft = styled(ScrollButtonBase)`
  left: 0;
  transform: translateX(-50%) translateY(-50%);
`;

const ScrollButtonRight = styled(ScrollButtonBase)`
  right: 0;
  transform: translateX(50%) translateY(-50%);
`;

const SCROLL_AMOUNT = 150;

const ActivateAccountPage = () => {
  const searchParams = useSearchParams();
  const { t } = useTranslation(['pages']);
  const { mutate: activateAccount, isPending } = useActivateAccount();
  const uid = searchParams.get('uid');
  const { data: invitation, isLoading: isInvitationLoading } = useGetInvitation(uid);

  const isNewUser = invitation?.new_user ?? true;
  const brandName = invitation?.brand_name;
  const regionName = invitation?.region_name;
  const existingAccounts = invitation?.existing_accounts;

  const accountsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateAccountsScrollState = () => {
    const el = accountsContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    requestAnimationFrame(updateAccountsScrollState);
    window.addEventListener('resize', updateAccountsScrollState);
    return () => window.removeEventListener('resize', updateAccountsScrollState);
  }, [existingAccounts]);

  const validationSchema = Yup.object({
    email: Yup.string().email(t('validation:invalidEmail')).required(t('validation:required')),
    verification_code: Yup.string()
      .matches(/^\d{6}$/, t('validation:invalidVerificationCode'))
      .required(t('validation:required')),
    ...(isNewUser && {
      name: Yup.string()
        .min(3, t('validation:atLeastChars', { number: 3 }))
        .required(t('validation:required')),
      last_name: Yup.string()
        .min(3, t('validation:atLeastChars', { number: 3 }))
        .required(t('validation:required')),
      password: Yup.string()
        .min(8, t('validation:atLeastChars', { number: 8 }))
        .required(t('validation:required')),
      password_repeat: Yup.string()
        .oneOf([Yup.ref('password'), null], t('validation:passwordsMustMatch'))
        .required(t('validation:required')),
    }),
  });

  const shouldDisableEmailAndCode = Boolean(searchParams.get('email') && searchParams.get('code'));

  if (uid && isInvitationLoading) return null;

  return (
    <FullPageCardContainer
      xs="12"
      md="8"
      lg="6"
      footer={AuthFooter}
      variant="auth"
      cornerAction={{ label: 'Sign up', to: REGISTER }}
    >
      <h1 className="auth-page-title">{t('activateAccount.title')}</h1>
      <p className="auth-page-subtitle">
        {brandName ? (
          <>
            {t('activateAccount.invitedToManage')}{' '}
            <span style={{ fontWeight: 600 }}>{brandName}</span>
          </>
        ) : (
          t('activateAccount.subtitle')
        )}
      </p>
      <Form
        initialValues={{
          email: searchParams.get('email') || '',
          verification_code: searchParams.get('code') || '',
          password: '',
          password_repeat: '',
          name: '',
          last_name: '',
        }}
        onSubmit={({ email, verification_code, name, last_name, password, password_repeat }) => {
          const payload = isNewUser
            ? { email, verification_code, name, last_name, password, password_repeat }
            : { email, verification_code };
          activateAccount(payload);
        }}
        validationSchema={validationSchema}
        renderDefaultButtons={false}
      >
        {(brandName || regionName) && (
          <BrandInfoBlock>
            {regionName && (
              <BrandInfoRow>
                <BrandInfoLabel>{t('activateAccount.region')}</BrandInfoLabel>
                <BrandInfoValue>{regionName}</BrandInfoValue>
              </BrandInfoRow>
            )}
          </BrandInfoBlock>
        )}
        <Form.Group>
          <Form.Label>{t('activateAccount.email')}</Form.Label>
          <Form.Input
            name="email"
            type="email"
            placeholder="johndoe@rambla.com"
            disabled={shouldDisableEmailAndCode}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{t('activateAccount.verificationCode')}</Form.Label>
          <Form.Input
            name="verification_code"
            type="text"
            placeholder=""
            disabled={shouldDisableEmailAndCode}
          />
        </Form.Group>
        {isNewUser && (
          <>
            <Form.Group>
              <Form.Label>{t('activateAccount.name')}</Form.Label>
              <Form.Input name="name" type="text" placeholder="John" />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('activateAccount.lastName')}</Form.Label>
              <Form.Input name="last_name" type="text" placeholder="Doe" />
            </Form.Group>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>{t('activateAccount.password')}</Form.Label>
                </Col>
              </Row>
              <Form.Input type="password" name="password" placeholder="" />
            </Form.Group>
            <Form.Group>
              <Row>
                <Col>
                  <Form.Label>{t('activateAccount.rePassword')}</Form.Label>
                </Col>
              </Row>
              <Form.Input type="password" name="password_repeat" placeholder="" />
            </Form.Group>
          </>
        )}
        <Wrapper mb-3>
          <Form.Submit
            size="lg"
            variant="primary"
            block
            ignoreClickedState
            isLoading={isPending}
            style={{ width: '100%' }}
          >
            {t('activateAccount.activateAccount')}
          </Form.Submit>
        </Wrapper>
      </Form>

      {existingAccounts?.length > 0 && (
        <AccountsSection>
          <AccountsSectionHeader>
            {t('activateAccount.existingAccountsHeader')}
          </AccountsSectionHeader>
          <AccountsWrapper>
            <AccountsScrollContainer
              ref={accountsContainerRef}
              onScroll={updateAccountsScrollState}
            >
              {existingAccounts.map((account) => (
                <AccountCard key={`${account.brand_name}-${account.region_name}`}>
                  <AccountAvatar>
                    {account.picture ? (
                      <img src={account.picture} alt="" />
                    ) : (
                      `${account.name?.[0] ?? ''}${account.last_name?.[0] ?? ''}`
                    )}
                  </AccountAvatar>
                  <AccountInfo>
                    <AccountName>
                      {account.name} {account.last_name}
                    </AccountName>
                    <AccountMeta>
                      {account.brand_name} · {account.region_name}
                    </AccountMeta>
                  </AccountInfo>
                </AccountCard>
              ))}
            </AccountsScrollContainer>

            {canScrollLeft && (
              <ScrollButtonLeft
                onClick={() =>
                  accountsContainerRef.current?.scrollBy({
                    left: -SCROLL_AMOUNT,
                    behavior: 'smooth',
                  })
                }
                aria-label="Scroll left"
              >
                <MdArrowBack size="16px" color="#333333" />
              </ScrollButtonLeft>
            )}
            {canScrollRight && (
              <ScrollButtonRight
                onClick={() =>
                  accountsContainerRef.current?.scrollBy({
                    left: SCROLL_AMOUNT,
                    behavior: 'smooth',
                  })
                }
                aria-label="Scroll right"
              >
                <MdArrowForward size="16px" color="#333333" />
              </ScrollButtonRight>
            )}
          </AccountsWrapper>
        </AccountsSection>
      )}
    </FullPageCardContainer>
  );
};

export default ActivateAccountPage;
